import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Calendar as CalendarIcon, Clock, CheckCircle, Loader2, AlertCircle, CalendarPlus } from 'lucide-react';
import axios from 'axios';
import logger from '../utils/logger';
import { trackBookingCompleted } from '../utils/analytics';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Format a Date object to local YYYY-MM-DD (avoids UTC day-shift from toISOString)
const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const VEHICLE_LABELS = {
  tungbil: 'Tungbil/Lastebil',
  tilhenger: 'Tilhenger',
  personbil: 'Personbil',
};

// --- Norwegian registration plate helpers (free, client-side) ---
// Standard car/truck/trailer plates: 2 letters + 5 digits (e.g. "AB 12345").
// MC / moped / tractor plates: 2 letters + 4 digits (e.g. "DT 1234").
// Canonical (compact, no space) form is sent to the backend for consistent vehicle matching.
const canonicalReg = (raw) => (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const formatRegNumber = (raw) => {
  let v = canonicalReg(raw).slice(0, 7); // max 2 letters + 5 digits
  const m = v.match(/^([A-Z]{2})(\d+)$/);
  return m ? `${m[1]} ${m[2]}` : v;
};

const isValidNorwegianPlate = (raw) => /^[A-Z]{2}\d{4,5}$/.test(canonicalReg(raw));

/**
 * Reusable 3-step booking flow (date -> time -> details).
 * Used by both BookingModal (mode="modal") and the standalone BookingPage (mode="standalone").
 *
 * Props:
 *  - mode: "modal" | "standalone" (default "modal")
 *  - initialVehicleType: pre-select vehicle type ("tungbil" | "tilhenger" | "personbil")
 *  - initialNotes: pre-fill the notes field (e.g. from ?service= param)
 *  - onSuccess: callback(booking) fired after a successful booking
 *  - active: whether the flow is visible/active (used by modal to trigger analytics)
 */
const BookingFlow = ({
  mode = 'modal',
  initialVehicleType = '',
  initialNotes = '',
  onSuccess,
  active = true,
}) => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availability, setAvailability] = useState(null); // { is_closed, slots: [...] }
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [cutoffHours, setCutoffHours] = useState(72);
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [closedDates, setClosedDates] = useState([]);
  const [nextAvailable, setNextAvailable] = useState(null); // { date, time }
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    regNumber: '',
    vehicleType: initialVehicleType || '',
    notes: initialNotes || '',
  });
  const { toast } = useToast();

  // Fetch booking cutoff so the calendar can disable too-soon dates
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/slots/settings`)
      .then((res) => {
        if (cancelled) return;
        if (typeof res.data?.cutoff_hours === 'number') {
          setCutoffHours(res.data.cutoff_hours);
        }
        if (Array.isArray(res.data?.working_days) && res.data.working_days.length) {
          setWorkingDays(res.data.working_days);
        }
      })
      .catch(() => {});

    // Fetch next-available date + upcoming closed dates (public endpoint)
    axios
      .get(`${API}/slots/next-available`)
      .then((res) => {
        if (cancelled) return;
        if (Array.isArray(res.data?.closed_dates)) {
          setClosedDates(res.data.closed_dates);
        }
        if (res.data?.next_available_date) {
          setNextAvailable({ date: res.data.next_available_date, time: res.data.next_available_time });
          // Open the calendar on the month of the first bookable date
          const parts = res.data.next_available_date.split('-');
          setCalendarMonth(new Date(Number(parts[0]), Number(parts[1]) - 1, 1));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Earliest date a public customer can book. Mirrors the backend cutoff: only OPEN
  // days count toward the cutoff hours, so closed weekends AND closed dates (holidays)
  // "pause" the clock (e.g. a Friday visitor cannot reach the following Monday, and a
  // summer-holiday period pushes the first bookable date past it).
  const minBookableDate = (() => {
    let remaining = cutoffHours;
    const cursor = new Date();
    let guard = 0;
    while (remaining > 0 && guard < 400) {
      guard += 1;
      const nextMidnight = new Date(cursor);
      nextMidnight.setHours(0, 0, 0, 0);
      nextMidnight.setDate(nextMidnight.getDate() + 1);
      const hoursUntilMidnight = (nextMidnight - cursor) / 3600000;
      // JS getDay(): 0=Sun..6=Sat; backend isoweekday: 1=Mon..7=Sun
      const isoDay = cursor.getDay() === 0 ? 7 : cursor.getDay();
      const isOpen = workingDays.includes(isoDay) && !closedDates.includes(formatDate(cursor));
      if (isOpen) {
        if (remaining <= hoursUntilMidnight) {
          // Cutoff falls on this open day -> allow the whole day in the calendar
          // (the backend filters out the too-early time slots on this boundary day).
          const boundary = new Date(cursor);
          boundary.setHours(0, 0, 0, 0);
          return boundary;
        }
        remaining -= hoursUntilMidnight;
      }
      cursor.setTime(nextMidnight.getTime());
    }
    cursor.setHours(0, 0, 0, 0);
    return cursor;
  })();

  // The backend's next_available_date is the authoritative earliest bookable date
  // (it already accounts for cutoff, working days, closed dates AND per-slot capacity).
  // Prefer it as the calendar floor; fall back to the client estimate before it loads.
  const effectiveMinDate = nextAvailable?.date
    ? new Date(`${nextAvailable.date}T00:00:00`)
    : minBookableDate;

  // Keep pre-fills in sync if props arrive after mount (e.g. async query parsing)
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      vehicleType: prev.vehicleType || initialVehicleType || '',
      notes: prev.notes || initialNotes || '',
    }));
  }, [initialVehicleType, initialNotes]);

  const fetchAvailability = useCallback(async (date) => {
    setLoadingSlots(true);
    setAvailability(null);
    try {
      const res = await axios.get(`${API}/slots/availability/${formatDate(date)}`);
      setAvailability(res.data);
    } catch (error) {
      logger.error('error', 'Failed to fetch availability', { error: error.message });
      setAvailability({ is_closed: false, slots: [] });
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  const handleDateSelect = (date) => {
    // shadcn Calendar (single mode) passes undefined when re-clicking the selected date.
    // Fall back to the already-selected date so the flow still advances.
    const chosen = date || selectedDate;
    if (!chosen) return;
    setSelectedDate(chosen);
    setSelectedTime(null);
    setStep(2);
    fetchAvailability(chosen);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegChange = (e) => {
    setFormData({ ...formData, regNumber: formatRegNumber(e.target.value) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vehicleType) {
      toast({
        title: 'Kjøretøytype mangler',
        description: 'Vennligst velg kjøretøytype.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.notes || !formData.notes.trim()) {
      toast({
        title: 'Beskjed mangler',
        description: 'Vennligst skriv kort hva du ønsker å få gjort med bilen.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        customer_name: formData.name,
        customer_phone: formData.phone,
        customer_email: formData.email,
        vehicle_type: formData.vehicleType,
        reg_number: canonicalReg(formData.regNumber),
        date: formatDate(selectedDate),
        time_slot: selectedTime,
        notes: formData.notes.trim(),
        booking_source: 'website',
        booking_source_detail: mode === 'standalone' ? 'standalone_booking_page' : 'website_modal',
        // slot_number omitted -> backend auto-assigns
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      const booking = response.data;

      logger.userAction('booking_created', {
        booking_number: booking.booking_number,
        date: bookingData.date,
        time: bookingData.time_slot,
        source: bookingData.booking_source_detail,
      });
      trackBookingCompleted(formData.vehicleType, bookingData.date);

      if (mode === 'standalone') {
        // Inline confirmation screen (user may stay on the page)
        setConfirmedBooking(booking);
        setStep(4);
      } else {
        toast({
          title: 'Booking bekreftet!',
          description: `Din bookingnummer: ${booking.booking_number}. Vi sender bekreftelse på SMS.`,
        });
        // Reset for next time the modal opens
        setStep(1);
        setSelectedDate(null);
        setSelectedTime(null);
        setFormData({ name: '', phone: '', email: '', regNumber: '', vehicleType: initialVehicleType || '', notes: initialNotes || '' });
      }

      if (onSuccess) onSuccess(booking);
    } catch (error) {
      logger.error('error', 'Booking failed', { error: error.message });
      toast({
        title: 'Booking feilet',
        description: error.response?.data?.detail || 'Noe gikk galt. Vennligst prøv igjen.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailability(null);
    setConfirmedBooking(null);
    setFormData({ name: '', phone: '', email: '', regNumber: '', vehicleType: initialVehicleType || '', notes: initialNotes || '' });
  };

  const timeSlots = availability?.slots || [];

  return (
    <div data-testid="booking-flow">
      {/* Progress Steps (hidden on confirmation) */}
      {step < 4 && (
        <div className="flex items-center justify-center space-x-4 mb-8" data-testid="booking-steps">
          {[
            { n: 1, label: 'Velg dato' },
            { n: 2, label: 'Velg tid' },
            { n: 3, label: 'Dine detaljer' },
          ].map((s, idx) => (
            <React.Fragment key={s.n}>
              {idx > 0 && <div className="w-12 h-0.5 bg-gray-700"></div>}
              <div className={`flex items-center space-x-2 ${step >= s.n ? 'text-red-400' : 'text-gray-600'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= s.n ? 'bg-red-600 text-white' : 'bg-gray-700'}`}>
                  {step > s.n ? <CheckCircle className="w-5 h-5" /> : s.n}
                </div>
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Step 1: Date Selection */}
      {step === 1 && (
        <div className="space-y-4" data-testid="booking-step-date">
          <div className="flex items-center space-x-2 mb-4">
            <CalendarIcon className="w-5 h-5 text-red-400" />
            <h3 className="text-xl font-bold">Velg en dato</h3>
          </div>

          {nextAvailable?.date && (
            <div className="bg-green-600/10 border border-green-600/40 rounded-xl p-3 text-center" data-testid="booking-next-available">
              <p className="text-sm text-green-300">
                Første ledige time:{' '}
                <span className="font-semibold text-white">
                  {new Date(nextAvailable.date).toLocaleDateString('no-NO', { weekday: 'long', day: 'numeric', month: 'long' })}
                  {nextAvailable.time ? ` kl. ${nextAvailable.time}` : ''}
                </span>
              </p>
              {closedDates.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">Stengte dager (ferie/helligdager) er markert som utilgjengelige.</p>
              )}
            </div>
          )}

          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              disabled={(date) =>
                date < effectiveMinDate ||
                date.getDay() === 0 ||
                date.getDay() === 6 ||
                closedDates.includes(formatDate(date))
              }
              className="rounded-xl border border-gray-700 bg-gray-800/50"
            />
          </div>
        </div>
      )}

      {/* Step 2: Time Selection (REAL availability) */}
      {step === 2 && (
        <div className="space-y-6" data-testid="booking-step-time">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-red-400" />
              <h3 className="text-xl font-bold">Velg tidspunkt</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-gray-400 hover:text-white" data-testid="booking-back-to-date">
              ← Tilbake
            </Button>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <p className="text-sm text-gray-400">
              Valgt dato: <span className="text-white font-semibold">{selectedDate?.toLocaleDateString('no-NO')}</span>
            </p>
            <p className="text-sm text-gray-400 mt-1">Velg et ledig tidspunkt. Vi tildeler plass automatisk.</p>
          </div>

          {loadingSlots && (
            <div className="flex items-center justify-center py-12 text-gray-400" data-testid="booking-slots-loading">
              <Loader2 className="w-6 h-6 mr-2 animate-spin" /> Henter ledige tider...
            </div>
          )}

          {!loadingSlots && availability?.is_closed && (
            <div className="flex items-center justify-center py-12 text-yellow-400 text-center" data-testid="booking-date-closed">
              <AlertCircle className="w-6 h-6 mr-2" /> Vi har stengt denne dagen. Velg en annen dato.
            </div>
          )}

          {!loadingSlots && !availability?.is_closed && timeSlots.length === 0 && (
            <div className="flex items-center justify-center py-12 text-gray-400 text-center" data-testid="booking-no-slots">
              <AlertCircle className="w-6 h-6 mr-2" /> Ingen ledige tider denne dagen. Velg en annen dato.
            </div>
          )}

          {!loadingSlots && !availability?.is_closed && timeSlots.length > 0 && (
            <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="booking-slots-list">
              {timeSlots.map((slot) => {
                const hasAvailability = slot.available > 0;
                return (
                  <button
                    key={slot.time}
                    onClick={() => hasAvailability && handleTimeSelect(slot.time)}
                    disabled={!hasAvailability}
                    data-testid={`booking-slot-${slot.time}`}
                    className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ${
                      hasAvailability
                        ? 'bg-green-600/10 border-green-600/50 hover:bg-green-600/20 hover:border-green-600'
                        : 'bg-gray-800/30 border-gray-700/50 cursor-not-allowed opacity-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Clock className={`w-5 h-5 ${hasAvailability ? 'text-green-400' : 'text-gray-600'}`} />
                        <span className={`text-lg font-bold ${hasAvailability ? 'text-white' : 'text-gray-600'}`}>{slot.time}</span>
                      </div>
                      <div className="text-right">
                        {hasAvailability ? (
                          <Badge className="bg-green-600/20 text-green-400 border-green-600/50">
                            {slot.available} {slot.available === 1 ? 'ledig plass' : 'ledige plasser'}
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-600/20 text-gray-500 border-gray-600/50">Fullbooket</Badge>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Contact Form */}
      {step === 3 && (
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-step-details">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold">Dine kontaktdetaljer</h3>
            <Button type="button" variant="ghost" size="sm" onClick={() => setStep(2)} className="text-gray-400 hover:text-white" data-testid="booking-back-to-time">
              ← Tilbake
            </Button>
          </div>

          {/* Booking Summary */}
          <div className="bg-gradient-to-r from-red-600/20 to-yellow-600/20 rounded-xl p-4 border border-red-600/30">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Dato</p>
                <p className="text-white font-semibold">{selectedDate?.toLocaleDateString('no-NO')}</p>
              </div>
              <div>
                <p className="text-gray-400">Tidspunkt</p>
                <p className="text-white font-semibold">{selectedTime}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Fullt navn *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required className="bg-gray-800 border-gray-700 text-white" placeholder="Ola Nordmann" data-testid="booking-input-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefonnummer *</Label>
              <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required className="bg-gray-800 border-gray-700 text-white" placeholder="+47 123 45 678" data-testid="booking-input-phone" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-post *</Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required className="bg-gray-800 border-gray-700 text-white" placeholder="ola@example.com" data-testid="booking-input-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vehicleType">Kjøretøytype *</Label>
              <Select value={formData.vehicleType} onValueChange={(value) => setFormData({ ...formData, vehicleType: value })} required>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="booking-select-vehicle">
                  <SelectValue placeholder="Velg type" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="tungbil">Tungbil/Lastebil</SelectItem>
                  <SelectItem value="tilhenger">Tilhenger</SelectItem>
                  <SelectItem value="personbil">Personbil</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="regNumber">Kjennemerke (reg.nr) *</Label>
              <div className="relative">
                <Input
                  id="regNumber"
                  name="regNumber"
                  value={formData.regNumber}
                  onChange={handleRegChange}
                  required
                  maxLength={8}
                  autoComplete="off"
                  autoCapitalize="characters"
                  className={`bg-gray-800 border-gray-700 text-white uppercase tracking-wider pr-9 ${
                    formData.regNumber && isValidNorwegianPlate(formData.regNumber) ? 'border-green-500/60' : ''
                  }`}
                  placeholder="AB 12345"
                  data-testid="booking-input-regnumber"
                />
                {formData.regNumber && isValidNorwegianPlate(formData.regNumber) && (
                  <CheckCircle className="w-4 h-4 text-green-400 absolute right-3 top-1/2 -translate-y-1/2" data-testid="booking-regnumber-valid" />
                )}
              </div>
              <p className={`text-xs ${
                formData.regNumber && !isValidNorwegianPlate(formData.regNumber) ? 'text-yellow-400/80' : 'text-gray-500'
              }`}>
                {formData.regNumber && !isValidNorwegianPlate(formData.regNumber)
                  ? 'Dobbeltsjekk skiltet (vanlig format: AB 12345)'
                  : 'Norsk skilt, f.eks. AB 12345'}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Beskjed *</Label>
            <p className="text-xs text-gray-400">
              Skriv kort hva du ønsker å få gjort med bilen: service, EU-kontroll, reparasjon, feilsøking eller annet. Er du usikker, kan du kort beskrive hva som er problemet.
            </p>
            <textarea id="notes" name="notes" value={formData.notes} onChange={handleInputChange} rows={3} required className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="F.eks. EU-kontroll, oljeservice, eller bilen bråker ved bremsing..." data-testid="booking-input-notes" />
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-lg py-6 rounded-xl" data-testid="booking-submit-btn">
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Bekrefter booking...</>
            ) : (
              <><CheckCircle className="w-5 h-5 mr-2" /> Bekreft booking</>
            )}
          </Button>
        </form>
      )}

      {/* Step 4: Inline Confirmation (standalone only) */}
      {step === 4 && confirmedBooking && (
        <div className="text-center space-y-6 py-4" data-testid="booking-confirmation">
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-green-600/20 border-2 border-green-500 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-black text-white mb-2">Booking bekreftet!</h3>
            <p className="text-gray-400">Vi har mottatt forespørselen din og sender en bekreftelse på SMS/e-post.</p>
          </div>

          <div className="bg-gradient-to-r from-red-600/20 to-yellow-600/20 rounded-xl p-6 border border-red-600/30 text-left max-w-md mx-auto">
            <div className="mb-4 pb-4 border-b border-white/10">
              <p className="text-gray-400 text-sm">Referansenummer</p>
              <p className="text-2xl font-black text-yellow-400" data-testid="booking-confirmation-number">{confirmedBooking.booking_number}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Dato</p>
                <p className="text-white font-semibold" data-testid="booking-confirmation-date">
                  {confirmedBooking.date ? new Date(confirmedBooking.date).toLocaleDateString('no-NO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-400">Tidspunkt</p>
                <p className="text-white font-semibold" data-testid="booking-confirmation-time">{confirmedBooking.time_slot}</p>
              </div>
              <div>
                <p className="text-gray-400">Kjøretøytype</p>
                <p className="text-white font-semibold" data-testid="booking-confirmation-vehicle">{VEHICLE_LABELS[confirmedBooking.vehicle_type] || confirmedBooking.vehicle_type}</p>
              </div>
              <div>
                <p className="text-gray-400">Kjennemerke</p>
                <p className="text-white font-semibold" data-testid="booking-confirmation-regnumber">{confirmedBooking.reg_number || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400">Navn</p>
                <p className="text-white font-semibold">{confirmedBooking.customer_name}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={`${API}/bookings/${confirmedBooking.booking_number}/calendar.ics`}
              className="inline-flex items-center justify-center bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold px-8 py-3 rounded-xl transition-all duration-300"
              data-testid="booking-add-to-calendar"
            >
              <CalendarPlus className="w-5 h-5 mr-2" />
              Legg til i kalender
            </a>
            <Button onClick={resetFlow} variant="outline" className="border-2 border-white/30 text-white hover:bg-white hover:text-black font-bold px-8 py-5 rounded-xl" data-testid="booking-new-btn">
              Book en ny time
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingFlow;
